import React from 'react';
import { Column } from 'react-table';
import { formatDate } from '../../../utilities/dateHelpers';


const formatNumber = (value: number | string | null | undefined, maxFraction = 2): string => {
  if (value == null || value === '') return '-';
  const num = Number(value);
  if (Number.isNaN(num)) return '-';
  return num.toLocaleString(undefined, { maximumFractionDigits: maxFraction });
};

export const findAssetByWellName = (wellName: string, assets: any[]) => {
  if (!wellName) return undefined;
  const lower = wellName.toLowerCase();
  return assets.find((a: any) => a.assetName?.toLowerCase() === lower);
};

export const handleWellFieldClick = (wellName: string, source: 'wellname' | 'testdate', assets: any[]) => {
  const baseUrl = window.location.origin;
  const asset = findAssetByWellName(wellName, assets);
  if (!asset?.assetId) return;

  if (source === 'wellname') {
    if (!asset.groupName) return;
    const encodedGroup = encodeURIComponent(asset.groupName);
    const targetUrl = `${baseUrl}/asset/status/${asset.assetId}/${encodedGroup}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  } else {
    const targetUrl = `${baseUrl}/pumpchecker/ESPDiagnosis/${asset.assetId}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }
};

// ========================
// Top 5 Frequency (Speed) Change Opportunities
// ========================
export const frequencyRateTableColumns = (assets: any[]): Column<any>[] => [
  // Standalone columns (no group header)
  {
    Header: 'Well',
    accessor: 'WellName',
    Cell: ({ value }: { value: string }) => (
      <a className='table-row-link well-name' onClick={() => handleWellFieldClick(value, 'wellname', assets)}>
        {value ?? '-'}
      </a>
    ),
  },
  {
    Header: 'Date',
    accessor: 'TestDate',
    Cell: ({ value }: any) => <span>{formatDate(value)}</span>,
  },
  {
    Header: 'Rate (stb/d)',
    accessor: 'Flowrate',
    Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
  },
  {
    Header: 'WC (%)',
    accessor: 'WaterCut',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },
  {
    Header: 'Oil rate (stb/d)',
    accessor: 'Oil',
    Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
  },
  {
    Header: 'PIP (psig)',
    accessor: 'PumpIntakePressure',
    Cell: ({ value }: any) => <span>{formatNumber(value, 1)}</span>,
  },
  {
    Header: 'Freq. (Hz)',
    accessor: 'Frequency',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },
  {
    Header: 'Deg (%)',
    accessor: 'Degradation',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },

  // Grouped right block
  {
    Header: 'Speed change opportunity',
    columns: [
      {
        Header: 'New freq. (hz)',
        accessor: 'NewFrequency',
        Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
      },
      {
        Header: 'FC rate (stb/d)',
        accessor: 'FrequencyChangeRate',
        Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
      },
      {
        Header: 'FC add oil (stb/d)',
        accessor: 'FrequencyChangeAdditionalOil',
        Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
      },
    ],
  },
];

// ========================
// Top 5 Pump Change Opportunities
// ========================
export const pumpChangeTableColumns = (assets: any[]): Column<any>[] => [
  // Standalone columns
  {
    Header: 'Well',
    accessor: 'WellName',
    Cell: ({ value }: { value: string }) => (
      <a className='table-row-link well-name' onClick={() => handleWellFieldClick(value, 'wellname', assets)}>
        {value ?? '-'}
      </a>
    ),
  },
  {
    Header: 'Date',
    accessor: 'TestDate',
    Cell: ({ value }: any) => <span>{formatDate(value)}</span>,
  },
  {
    Header: 'Rate (stb/d)',
    accessor: 'Flowrate',
    Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
  },
  {
    Header: 'WC (%)',
    accessor: 'WaterCut',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },
  {
    Header: 'Oil rate (stb/d)',
    accessor: 'Oil',
    Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
  },
  {
    Header: 'PIP (psig)',
    accessor: 'PumpIntakePressure',
    Cell: ({ value }: any) => <span>{formatNumber(value, 1)}</span>,
  },
  {
    Header: 'Freq. (Hz)',
    accessor: 'Frequency',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },
  {
    Header: 'Deg (%)',
    accessor: 'Degradation',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },

  // Grouped right block
  {
    Header: 'Upsizing opportunity',
    columns: [
      {
        Header: 'Nominal rate (stb/d)',
        accessor: 'NominalRate',
        Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
      },
      {
        Header: 'PC rate (stb/d)',
        accessor: 'PumpChangeRate',
        Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
      },
      {
        Header: 'PC add oil (stb/d)',
        accessor: 'AdditionalOil',
        Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
      },
    ],
  },
];

// ========================
// Producers (Top/Bottom) — simple columns (kept for convenience)
// ========================
export const producerTop5Columns = (assets: any[]): Column<any>[] => [
  {
    Header: 'Well',
    accessor: 'WellName',
    Cell: ({ value }: { value: string }) => (
      <a className='table-row-link well-name' onClick={() => handleWellFieldClick(value, 'wellname', assets)}>
        {value ?? '-'}
      </a>
    ),
  },
  {
    Header: 'Date',
    accessor: 'TestDate',
    Cell: ({ value }: any) => <span>{formatDate(value)}</span>,
  },
  {
    Header: 'Rate (stb/d)',
    accessor: 'Flowrate',
    Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
  },
  {
    Header: 'WC (%)',
    accessor: 'WaterCut',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : Number(value).toFixed(1)}</span>,
  },
  {
    Header: 'Oil rate (stb/d)',
    accessor: 'Oil',
    Cell: ({ value }: any) => <span>{formatNumber(value, 0)}</span>,
  },
  {
    Header: 'PIP (psig)',
    accessor: 'PumpIntakePressure',
    Cell: ({ value }: any) => <span>{formatNumber(value, 1)}</span>,
  },
  {
    Header: 'DD (%)',
    accessor: 'Drawdown',
    Cell: ({ value }: any) => <span>{value == null || value === '' ? '-' : `${Number(value).toFixed(1)}`}</span>,
  },
];

export const producerBottom5Columns = (assets: any[]): Column<any>[] => producerTop5Columns(assets);
